import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { finance } from '@/api/client';
import { fmtMoney } from '@/lib/money';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { errorText } from '@/lib/permissionError';

/**
 * DREAM40 AC4/AC5 — Generate Invoice. Study is pre-filled from the card
 * that opened it; the date range is pre-filled with the page's current
 * period but stays an explicit, editable, required field. AC8 is
 * irreversible (no un-flag mechanism), so a preview + a second explicit
 * click are required before the request fires.
 */
export function GenerateInvoiceModal({
  open,
  onOpenChange,
  studyId,
  studyLabel,
  defaultFrom,
  defaultTo,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  studyId: string;
  studyLabel: string;
  defaultFrom: string;
  defaultTo: string;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [periodFrom, setPeriodFrom] = useState(defaultFrom);
  const [periodTo, setPeriodTo] = useState(defaultTo);
  const [format, setFormat] = useState<'pdf' | 'csv'>('pdf');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof finance.generateInvoice>> | null>(null);

  const rangeValid = !!periodFrom && !!periodTo && periodFrom <= periodTo;

  const preview = useQuery({
    queryKey: ['finance-invoice-preview', user?.activeSiteId, studyId, periodFrom, periodTo],
    queryFn: () => finance.previewInvoice({ study_id: studyId, period_from: periodFrom, period_to: periodTo }),
    enabled: open && rangeValid,
  });

  const generate = useMutation({
    mutationFn: () => finance.generateInvoice({ study_id: studyId, period_from: periodFrom, period_to: periodTo, format }),
    onSuccess: (r) => {
      setResult(r);
      if (r.generated) {
        toast.success(`Revenue report ${r.invoice_number} generated`);
        window.open(r.download_url, '_blank', 'noopener,noreferrer');
        qc.invalidateQueries({ queryKey: ['finance-study-cards', user?.activeSiteId] });
        qc.invalidateQueries({ queryKey: ['finance-invoices', user?.activeSiteId] });
      }
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to generate revenue report'));
    },
  });

  function close() {
    setConfirming(false);
    setResult(null);
    onOpenChange(false);
  }

  return (
    <Modal open={open} onOpenChange={close} title="Generate revenue report" description={studyLabel} width={680}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label text-xs">Period from</label>
            <input
              type="date"
              className="input"
              value={periodFrom}
              onChange={(e) => { setPeriodFrom(e.target.value); setConfirming(false); setResult(null); }}
            />
          </div>
          <div>
            <label className="label text-xs">Period to</label>
            <input
              type="date"
              className="input"
              value={periodTo}
              onChange={(e) => { setPeriodTo(e.target.value); setConfirming(false); setResult(null); }}
            />
          </div>
        </div>
        {!rangeValid && (periodFrom || periodTo) && (
          <p className="text-xs text-danger-700">End date must be on or after the start date.</p>
        )}

        <div>
          <label className="label text-xs">Format</label>
          <div className="flex gap-2">
            <button
              type="button"
              className={format === 'pdf' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
              onClick={() => { setFormat('pdf'); setConfirming(false); setResult(null); }}
            >
              PDF
            </button>
            <button
              type="button"
              className={format === 'csv' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
              onClick={() => { setFormat('csv'); setConfirming(false); setResult(null); }}
            >
              Spreadsheet (CSV)
            </button>
          </div>
        </div>

        {rangeValid && !result && (
          <div className="rounded-lg border border-surface-border bg-surface-muted/50 p-3 text-sm">
            {preview.isLoading ? (
              <span className="text-ink-muted flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking eligible visits…
              </span>
            ) : preview.data ? (
              <>
                <div>
                  <strong>{preview.data.eligible_count}</strong> visit{preview.data.eligible_count === 1 ? '' : 's'} ·{' '}
                  <strong>{fmtMoney(preview.data.total_amount_cents, preview.data.currency_code)}</strong> will be included
                </div>
                {preview.data.visit_breakdown.length > 0 && (
                  <table className="mt-3 w-full text-xs">
                    <thead>
                      <tr className="border-b border-surface-border text-left text-[10px] uppercase tracking-wide text-ink-muted">
                        <th className="pb-1 pr-2 font-medium">Visit</th>
                        <th className="pb-1 pr-2 font-medium text-right">Fixed cost</th>
                        <th className="pb-1 pr-2 font-medium text-right">Subjects</th>
                        <th className="pb-1 pr-2 font-medium text-right">Per subject</th>
                        <th className="pb-1 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-border/50">
                      {preview.data.visit_breakdown.map((v) => (
                        <tr key={v.visit_name}>
                          <td className="py-1 pr-2 text-ink-primary">{v.visit_name}</td>
                          <td className="py-1 pr-2 text-right text-ink-muted">
                            {v.fixed_fee_cents > 0 ? fmtMoney(v.fixed_fee_cents, preview.data!.currency_code) : '—'}
                          </td>
                          <td className="py-1 pr-2 text-right text-ink-muted">{v.subject_count}</td>
                          <td className="py-1 pr-2 text-right text-ink-muted">
                            {fmtMoney(v.per_subject_cents, preview.data!.currency_code)}
                          </td>
                          <td className="py-1 text-right font-medium text-ink-primary">
                            {fmtMoney(v.subtotal_cents, preview.data!.currency_code)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-surface-border font-medium text-ink-primary">
                        <td className="pt-1.5" colSpan={4}>Total for all visits</td>
                        <td className="pt-1.5 text-right">
                          {fmtMoney(preview.data.total_amount_cents, preview.data.currency_code)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
                {preview.data.excluded.length > 0 && (
                  <details className="mt-2 text-xs text-ink-muted">
                    <summary className="cursor-pointer">{preview.data.excluded.length} visit(s) excluded</summary>
                    <ul className="mt-1 list-disc pl-4">
                      {preview.data.excluded.map((e) => (
                        <li key={e.visit_id}>{e.visit_reference} — {e.reason === 'rate_unresolved' ? 'no billable rate resolved' : 'not billable'}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            ) : null}
          </div>
        )}

        {result && !result.generated && (
          <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {result.reason === 'no_eligible_visits'
                ? 'No eligible visits in this range — nothing to report.'
                : `Too many visits (${result.eligible_count}) for a single revenue report run.`}
            </span>
          </div>
        )}

        {result?.generated && (
          <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800">
            Revenue report <strong>{result.invoice_number}</strong> generated for {fmtMoney(result.total_amount_cents, result.currency_code)}.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={close}>
            {result?.generated ? 'Close' : 'Cancel'}
          </button>
          {!result?.generated && (
            <button
              type="button"
              className="btn-primary"
              disabled={!rangeValid || !preview.data?.eligible_count || generate.isPending}
              onClick={() => {
                if (!confirming) {
                  setConfirming(true);
                  return;
                }
                generate.mutate();
              }}
            >
              {generate.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                </span>
              ) : confirming ? (
                'Generate revenue report — this cannot be undone'
              ) : (
                'Generate revenue report'
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
